import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import helpConfig from '../../content/help/config.json';
import { HelpConfig } from '../../types/Help';
import classNames from '../../utils/classNames';

const config = helpConfig as HelpConfig;

export default function HelpLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 md:pt-16">
          <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <nav className="flex-1 px-2 space-y-1">
                {/* Help Home Link */}
                <Link
                  to="/help"
                  className={classNames(
                    isActive('/help') && !location.pathname.match(/^\/help\/[^/]+/)
                      ? 'bg-wilma-100 text-wilma-600'
                      : 'text-gray-600 hover:bg-wilma-50 hover:text-wilma-600',
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md'
                  )}
                >
                  <span>Help Home</span>
                </Link>

                {/* Categories */}
                {config.categories.map((category) => (
                  <div key={category.id} className="mt-4">
                    <Link
                      to={`/help/${category.id}`}
                      className={classNames(
                        isActive(`/help/${category.id}`)
                          ? 'bg-wilma-100 text-wilma-600'
                          : 'text-gray-600 hover:bg-wilma-50 hover:text-wilma-600',
                        'group flex items-center px-3 py-2 text-sm font-medium rounded-md'
                      )}
                    >
                      <span>{category.title}</span>
                    </Link>
                    
                    {/* Category Pages */}
                    {isActive(`/help/${category.id}`) && (
                      <div className="ml-4 mt-1 space-y-1">
                        {category.pages.map((page) => (
                          <Link
                            key={page.id}
                            to={`/help/${category.id}/${page.id}`}
                            className={classNames(
                              location.pathname === `/help/${category.id}/${page.id}`
                                ? 'bg-wilma-100 text-wilma-600'
                                : 'text-gray-600 hover:bg-wilma-50 hover:text-wilma-600',
                              'group flex items-center px-3 py-2 text-sm font-medium rounded-md'
                            )}
                          >
                            <span className="text-xs">{page.title}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </aside>

        {/* Mobile sidebar button */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-600 hover:text-wilma-600"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="md:hidden fixed inset-0 z-40">
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-white">
              <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
                <nav className="flex-1 px-2 space-y-1">
                  <Link
                    to="/help"
                    onClick={() => setSidebarOpen(false)}
                    className={classNames(
                      isActive('/help') && !location.pathname.match(/^\/help\/[^/]+/)
                        ? 'bg-wilma-100 text-wilma-600'
                        : 'text-gray-600 hover:bg-wilma-50 hover:text-wilma-600',
                      'group flex items-center px-3 py-2 text-sm font-medium rounded-md'
                    )}
                  >
                    <span>Help Home</span>
                  </Link>

                  {config.categories.map((category) => (
                    <div key={category.id} className="mt-4">
                      <Link
                        to={`/help/${category.id}`}
                        onClick={() => setSidebarOpen(false)}
                        className={classNames(
                          isActive(`/help/${category.id}`)
                            ? 'bg-wilma-100 text-wilma-600'
                            : 'text-gray-600 hover:bg-wilma-50 hover:text-wilma-600',
                          'group flex items-center px-3 py-2 text-sm font-medium rounded-md'
                        )}
                      >
                        <span>{category.title}</span>
                      </Link>
                      
                      {isActive(`/help/${category.id}`) && (
                        <div className="ml-4 mt-1 space-y-1">
                          {category.pages.map((page) => (
                            <Link
                              key={page.id}
                              to={`/help/${category.id}/${page.id}`}
                              onClick={() => setSidebarOpen(false)}
                              className={classNames(
                                location.pathname === `/help/${category.id}/${page.id}`
                                  ? 'bg-wilma-100 text-wilma-600'
                                  : 'text-gray-600 hover:bg-wilma-50 hover:text-wilma-600',
                                'group flex items-center px-3 py-2 text-sm font-medium rounded-md'
                              )}
                            >
                              <span className="text-xs">{page.title}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 md:ml-64 pt-16 md:pt-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

